import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { SystemIcon } from '@helpers/icons';
import { Device, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import styled, { css } from 'styled-components';
const hoverStyles = css `
  color: var(--textColourHover, var(--contrastColour));
  background-color: var(--primaryColour);
  background-size: 100% 100%;
  border-color: var(--borderColourHover, var(--borderColour, var(--primaryColour)));

  ${() => IconWrapper} {
    color: var(--iconTextColour, var(--primaryColour));
    background-color: var(--iconBackgroundColour, var(--contrastColour));
  }

  &[data-icon-only='false'][data-icon-type=${SystemIcon.ArrowRight}] {
    ${() => IconWrapper} {
      transform: translateX(4px);
    }
  }

  &[data-icon-only='false'][data-icon-type=${SystemIcon.ArrowDown}] {
    ${() => IconWrapper} {
      transform: rotate(90deg) translateX(2px);
    }
  }
`;
export const DefaultButtonStyles = css `
  ${ButtonReset};
  ${transition('background-color, border-color, color', 0.2)};

  --buttonHeight: 44px;
  --primaryColour: ${brand.green.default};
  --contrastColour: ${brand.global.white};

  display: inline-flex;
  gap: 12px;
  align-items: center;
  border: 1px solid var(--borderColour, var(--primaryColour));
  background-color: var(--contrastColour);
  color: var(--textColour, var(--primaryColour));
  min-height: var(--buttonHeight);
  padding: 4px 16px;
  text-decoration: none;
  user-select: none;
  width: auto;
  border-radius: 30px;

  &[data-fill='right'] {
    background-position: left center;
  }

  &[data-fill='left'] {
    background-position: right center;
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('background-size, border-color, color, transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  span& {
    :focus-visible & {
      outline: auto;
      outline-offset: 5px;
      outline-width: 2px;
      outline-color: var(--textColour, var(--primaryColour));
    }

    button:focus-visible &,
    a:focus-visible &,
    button:hover &,
    a:hover & {
      ${hoverStyles};
    }
  }

  &:not(span) {
    cursor: pointer;

    &.focus-ring {
      outline: auto;
      outline-offset: 5px;
      outline-width: 2px;
      outline-color: var(--textColour, var(--primaryColour));
    }

    &.focus-ring,
    &:hover {
      ${hoverStyles};
    }
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    opacity: 0.66;
    background-size: 0% 100%;
    pointer-events: none;
  }
`;
export const SavingsButtonStyles = css `
  --primaryColour: ${brand.blue.default};
  --textColour: ${brand.blue.dark};
`;
export const TertiaryButtonStyles = css `
  --borderColour: ${brand.global.white};
  --borderColourHover: ${brand.blue.dark};
  --contrastColour: ${brand.blue.dark};
  --iconBackgroundColour: ${brand.green.default};
  --iconTextColour: ${brand.global.white};
  --primaryColour: ${brand.global.white};
`;
export const IntermediariesButtonStyles = css `
  --primaryColour: ${brand.blue.navy};
  --contrastColour: ${brand.green.light};
`;
const Container = styled.button `
  ${DefaultButtonStyles};

  &[data-type='default'] {
    --textColour: ${brand.green.dark};
  }

  &[data-type='savings'] {
    ${SavingsButtonStyles};
  }

  &[data-type='tertiary'] {
    ${TertiaryButtonStyles};
  }

  &[data-type='intermediaries'] {
    ${IntermediariesButtonStyles};
  }

  &[data-type='intermediaries-secondary'] {
    --borderColour: ${brand.blue.navy};
    --contrastColour: ${brand.global.white};
    --iconBackgroundColour: ${brand.blue.navy};
    --primaryColour: ${brand.green.light};
    --textColour: ${brand.blue.navy};
    --textColourHover: ${brand.blue.navy};
  }

  &[data-icon-only='true'] {
    height: var(--buttonHeight);
    justify-content: center;
    padding: 0;
    width: var(--buttonHeight);
  }
`;
export const CommonButtonTextStyles = css `
  ${transition('color', 0.2)};
  ${fonts.proximaNova.bold};

  color: currentColor;
  text-decoration: none;
  font-size: 18px;
  line-height: 26px;
`;
const Text = styled.span `
  ${CommonButtonTextStyles};

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
export const ButtonIconWrapperStyles = css `
  --padding: 3px;
  --iconHeight: 12px;

  display: grid;
  place-content: center;
  color: var(--iconTextColour, var(--contrastColour));
  background-color: var(--iconBackgroundColour, var(--primaryColour));
  margin: 0;
  height: calc(var(--iconHeight) + (var(--padding) * 2));
  width: calc(var(--iconHeight) + (var(--padding) * 2));
  border-radius: 5000px;

  > span,
  > svg {
    display: block;
    height: var(--iconHeight);
    width: var(--iconHeight);
  }
`;
const IconWrapper = styled.div `
  ${transition('color, background-color', 0.2)};
  ${ButtonIconWrapperStyles};

  [data-icon-position='left'] & {
    order: -1;
  }

  [data-icon-only='true'] & {
    --iconHeight: 24px;

    height: calc(var(--buttonHeight) - 2px);
    width: var(--buttonHeight);
  }

  [data-icon-type=${SystemIcon.Loader}] & {
    --iconHeight: 16px
    --padding: 0;

    background-color: var(--iconTextColour, var(--contrastColour));
    color: var(--iconBackgroundColour, var(--primaryColour));
  }

  [data-icon-type=${SystemIcon.ArrowDown}] & {
    transform: rotate(90deg);
  }

  [data-icon-type=${SystemIcon.ArrowDown}] &,
  [data-icon-type=${SystemIcon.ArrowRight}] & {
    ${transition('transform, color, background-color', 0.2)};
  }
`;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
};
export default ButtonStyles;
